import React, { useState, useEffect } from 'react';
import { useClient } from '../context/ClientContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PlusCircle, AlertTriangle, TrendingDown, FileText, Sparkles, Pencil, Info, Trash2, Wheat } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { AIInsightsPanel } from '../components/AIInsightsPanel';
import { ModuleUploadZone } from '../components/ModuleUploadZone';
import { PendingImports } from '../components/PendingImports';

export const Suppliers = () => {
  const { selectedClient, API } = useClient();
  const [suppliers, setSuppliers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [mandiCompare, setMandiCompare] = useState(null);
  const [mandiPreview, setMandiPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!selectedClient) return;
    setLoading(true);
    try {
      const [supRes, invRes, anaRes, mvsRes, previewRes] = await Promise.all([
        axios.get(`${API}/suppliers/${selectedClient.id}`),
        axios.get(`${API}/suppliers/invoices/${selectedClient.id}`),
        axios.get(`${API}/suppliers/analysis/${selectedClient.id}`),
        axios.get(`${API}/insights/mandi-vs-supplier/${selectedClient.id}?days=180`).catch(() => ({ data: null })),
        axios.get(`${API}/mandi-mapping/${selectedClient.id}/preview?days=30`).catch(() => ({ data: null })),
      ]);
      setSuppliers(supRes.data.suppliers);
      setInvoices(invRes.data.invoices);
      setMandiCompare(mvsRes.data);
      setMandiPreview(previewRes.data);
      setAnalysis(anaRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedClient]);

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number || inv.id.slice(0,8)} (${inv.supplier_name} – ${inv.item_name})?`)) return;
    try {
      await axios.delete(`${API}/suppliers/invoices/${inv.id}`);
      fetchData();
    } catch (e) {
      alert('Delete failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  if (!selectedClient) return <div className="text-center text-text-secondary py-12">Select a client</div>;

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold text-primary tracking-tight">Supplier Management</h2>
          <p className="text-text-secondary mt-1">Track contracts & identify overpricing - {selectedClient.name}</p>
        </div>
        <div className="flex gap-2">
          <AddSupplierDialog clientId={selectedClient.id} onAdded={fetchData} />
          <AddInvoiceDialog clientId={selectedClient.id} suppliers={suppliers} onAdded={fetchData} />
        </div>
      </div>

      <Tabs defaultValue="analysis" className="space-y-6">
        <TabsList className="bg-white border border-border">
          <TabsTrigger value="analysis" data-testid="tab-analysis">Overpricing Analysis</TabsTrigger>
          <TabsTrigger value="ai-insights" data-testid="tab-supplier-ai-insights">
            <Sparkles className="h-3 w-3 mr-1 text-accent" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers & Rates</TabsTrigger>
          <TabsTrigger value="upload" data-testid="tab-supplier-upload">
            <Sparkles className="h-3 w-3 mr-1 text-state-info" /> AI Upload
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="space-y-4">
          <ModuleUploadZone
            module="suppliers"
            moduleLabel="Supplier Invoice"
            clientId={selectedClient.id}
            API={API}
            onUploadComplete={fetchData}
          />
          <PendingImports clientId={selectedClient.id} API={API} module="suppliers" onChange={fetchData} />
        </TabsContent>

        <TabsContent value="ai-insights">
          {selectedClient && (
            <AIInsightsPanel
              endpoint={`${API}/ai/insights/suppliers/${selectedClient.id}`}
              title="Supplier AI Insights"
              testId="supplier-ai-insights"
            />
          )}
        </TabsContent>

        <TabsContent value="analysis" className="space-y-4">
          {analysis && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SummaryCard label="Total Suppliers" value={analysis.summary.total_suppliers} />
                <SummaryCard label="Overpaying With" value={`${analysis.summary.suppliers_with_overpricing}/${analysis.summary.total_suppliers}`} />
                <SummaryCard label="Total Overpaid" value={`₹${analysis.summary.total_overpaid.toLocaleString()}`} highlight={analysis.summary.total_overpaid > 0} />
              </div>

              {analysis.suppliers_analysis.length === 0 ? (
                <div className="bg-white border border-border rounded-md p-8 text-center text-text-muted">
                  No supplier invoices yet. Add some to see overpricing analysis.
                </div>
              ) : (
                <div className="space-y-3">
                  {analysis.suppliers_analysis.map(sup => (
                    <div key={sup.supplier_id} className="bg-white border border-border rounded-md p-5" data-testid={`supplier-analysis-${sup.supplier_id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-lg font-semibold text-primary">{sup.supplier_name}</h4>
                          <p className="text-xs text-text-muted">{sup.invoice_count} invoices • Total spent: ₹{sup.total_spent.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          {sup.total_overpaid > 0 ? (
                            <>
                              <p className="text-xs uppercase tracking-wider text-state-error font-semibold">Overpaid</p>
                              <p className="text-xl font-bold text-state-error">₹{sup.total_overpaid.toLocaleString()}</p>
                              <p className="text-xs text-state-error">{sup.overpaid_percentage}% over agreed</p>
                            </>
                          ) : (
                            <span className="inline-flex items-center bg-green-50 text-state-success px-3 py-1 rounded-md text-sm font-semibold">
                              ✓ On Contract
                            </span>
                          )}
                        </div>
                      </div>
                      {sup.items_overpriced.length > 0 && (
                        <div className="bg-red-50 rounded-md p-3 mt-2">
                          <p className="text-xs uppercase tracking-wider text-state-error font-semibold mb-2">Top Overpriced Items</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {sup.items_overpriced.map((item, idx) => (
                              <div key={idx} className="text-sm flex justify-between">
                                <span>{item.item} <span className="text-text-muted text-xs">({item.date})</span></span>
                                <span className="font-semibold text-state-error">+₹{(item.overpaid ?? 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="invoices">
          {/* Iter-86 — Market benchmark strip (mapped items only). Shows the
              baseline-building/needs-review state; never the raw premium
              level (that comparison is structurally misleading — restaurant
              invoices are delivered prices). */}
          {mandiPreview && mandiPreview.rows && mandiPreview.rows.length > 0 && (
            <div className="mb-4 bg-white border border-border rounded-md p-4" data-testid="supplier-watch-market-strip">
              <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-primary">Market benchmarks — mapped items (last 30 days)</h3>
                  <p className="text-xs text-text-muted mt-0.5">
                    Signal is the <strong>shift vs this client's own baseline</strong>, not the raw level. Positive shift is
                    <strong> negotiation headroom</strong>, not recoverable money — mandi rates are a lever, never a contractual claim.
                  </p>
                </div>
                {mandiPreview.snapshot_latest && (
                  <span className="text-[11px] text-text-muted">
                    Prices last updated {new Date(mandiPreview.snapshot_latest).toLocaleDateString()}
                  </span>
                )}
              </div>
              <table className="w-full text-xs" data-testid="supplier-watch-market-table">
                <thead className="text-text-muted uppercase tracking-wider">
                  <tr>
                    <th className="text-left py-1">Item</th>
                    <th className="text-left py-1">Reference market</th>
                    <th className="text-left py-1">State</th>
                    <th className="text-right py-1">Headroom (30d)</th>
                    <th className="text-right py-1">Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {mandiPreview.rows.slice(0, 6).map((r) => {
                    const stateLabel = {
                      ready: { text: 'Ready', tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
                      baseline_building: {
                        text: `Baseline building · ${r.baseline_real_days}/${r.baseline_window_days} real days`,
                        tone: 'bg-amber-50 text-amber-800 border-amber-200',
                      },
                      needs_review: { text: 'Needs review — implausible premium', tone: 'bg-red-50 text-red-800 border-red-200' },
                    }[r.state] || { text: r.state, tone: 'bg-neutral-50 text-neutral-700 border-neutral-200' };
                    return (
                      <tr key={r.item_name} className="border-t border-border" data-testid={`market-row-${r.item_name}`}>
                        <td className="py-1.5 font-medium">{r.item_name}</td>
                        <td className="py-1.5 text-text-secondary">{r.commodity} · {r.market}</td>
                        <td className="py-1.5">
                          <span className={`px-2 py-0.5 rounded border ${stateLabel.tone}`}>
                            {stateLabel.text}
                          </span>
                        </td>
                        <td className="py-1.5 text-right">
                          {r.state === 'ready' && r.monthly_impact_inr > 0
                            ? `₹${r.monthly_impact_inr.toLocaleString('en-IN')}`
                            : '—'}
                        </td>
                        <td className="py-1.5 text-right text-text-muted">{r.lines_benchmarked}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {mandiPreview.coverage && (
                <p className="text-[11px] text-text-muted mt-2">
                  Benchmarked {mandiPreview.coverage.benchmarked} of {mandiPreview.coverage.total_lines} invoice lines this window.
                  Unmapped items and items awaiting baseline history are silently not compared.
                </p>
              )}
            </div>
          )}

          {/* Legacy Mandi Price Check widget (kept temporarily; language scrubbed to match spec). */}
          {mandiCompare && mandiCompare.flagged_count > 0 && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-md p-4" data-testid="mandi-vs-supplier-widget">
              <div className="flex items-start gap-3">
                <div className="bg-amber-100 rounded p-2">
                  <Wheat className="h-5 w-5 text-amber-700" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-900 mb-1">
                    Mandi premium check — {mandiCompare.flagged_count} lines &gt;15% above market
                  </h3>
                  <p className="text-xs text-amber-800 mb-3">
                    Cross-referenced {mandiCompare.invoices_scanned} invoices (last {mandiCompare.days} days) against {mandiCompare.mandi_rows_available.toLocaleString()} Mandi price points.
                    <strong> Estimated negotiation headroom (not recoverable): ₹{mandiCompare.total_overpay.toLocaleString()}</strong>.
                  </p>
                  <div className="grid grid-cols-1 gap-1 text-xs">
                    {(mandiCompare.flagged || []).slice(0, 5).map((f) => (
                      <div key={f.invoice_id} className="flex items-center justify-between bg-white px-3 py-1.5 rounded border border-amber-200" data-testid={`mandi-flag-${f.invoice_id}`}>
                        <div className="font-mono text-[11px]">
                          {f.date} · <span className="font-sans font-semibold">{f.supplier_name}</span> · {f.item_name}
                          <span className="text-text-muted ml-2">(vs {f.matched_commodity} @ {f.matched_market})</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono">paid ₹{f.unit_price_paid} / market ₹{f.mandi_modal_per_kg}</span>
                          <span className="ml-2 text-amber-700 font-semibold">+{f.overpay_pct}%</span>
                          <span className="ml-2 text-amber-800 font-bold">₹{f.total_overpay.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                    {mandiCompare.flagged_count > 5 && (
                      <p className="text-[11px] text-amber-700 mt-1 italic">…and {mandiCompare.flagged_count - 5} more.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="bg-white border border-border rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Invoice #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Supplier</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">Item</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Qty</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Unit Price</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Agreed</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider">Variance</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {invoices.length === 0 ? (
                    <tr><td colSpan="10" className="text-center text-text-muted py-8">No invoices yet</td></tr>
                  ) : invoices.map((inv, idx) => (
                    <tr key={inv.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-background-secondary'} data-testid={`invoice-${inv.id}`}>
                      <td className="px-4 py-3 text-sm">{inv.date}</td>
                      <td className="px-4 py-3 text-sm font-mono text-xs">{inv.invoice_number}</td>
                      <td className="px-4 py-3 text-sm">{inv.supplier_name}</td>
                      <td className="px-4 py-3 text-sm">{inv.item_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-right">{inv.quantity ?? '—'} {inv.unit || ''}</td>
                      <td className="px-4 py-3 text-sm text-right">₹{(inv.unit_price ?? 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-text-muted">{inv.agreed_unit_price ? `₹${inv.agreed_unit_price.toFixed(2)}` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-right font-semibold">₹{(inv.total_amount ?? 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-right">
                        {(inv.variance_amount ?? 0) > 0 ? (
                          <span className="text-state-error font-semibold">+₹{inv.variance_amount.toFixed(2)}</span>
                        ) : (inv.variance_amount ?? 0) < 0 ? (
                          <span className="text-state-success font-semibold">₹{inv.variance_amount.toFixed(2)}</span>
                        ) : (
                          <span className="text-text-muted">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleDeleteInvoice(inv)}
                          className="text-state-error hover:bg-red-50 rounded p-1"
                          title="Delete invoice"
                          data-testid={`delete-invoice-${inv.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="suppliers">
          {/* Data Source Notice */}
          <div className="bg-blue-50 border-l-4 border-state-info rounded-md p-4 mb-4" data-testid="suppliers-source-notice">
            <div className="flex items-start space-x-3">
              <Info className="h-5 w-5 text-state-info flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-state-info mb-1">Where do agreed rates come from?</p>
                <ul className="text-text-secondary space-y-1 text-xs">
                  <li>• <strong>Agreed Rates</strong>: Manually entered by you when onboarding a supplier (their contract rate). Click <Pencil className="inline h-3 w-3" /> to update.</li>
                  <li>• <strong>Actual Unit Price</strong>: Captured from each invoice you record - this is what you actually paid.</li>
                  <li>• <strong>Variance</strong>: Auto-calculated = (Actual - Agreed) × Quantity. Highlights overcharging.</li>
                  <li>• All rate changes are logged in price history with timestamp.</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suppliers.map(sup => (
              <SupplierCard key={sup.id} supplier={sup} onRefresh={fetchData} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

const SupplierCard = ({ supplier, onRefresh }) => {
  const [editing, setEditing] = useState(false);
  
  return (
    <>
      <div className="bg-white border border-border rounded-md p-5" data-testid={`supplier-card-${supplier.id}`}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="font-semibold text-primary">{supplier.name}</h4>
            <p className="text-xs text-text-muted uppercase tracking-wider mt-1">{supplier.category}</p>
          </div>
          <button 
            onClick={() => setEditing(true)}
            className="text-primary hover:bg-background-secondary rounded p-1"
            data-testid={`edit-supplier-${supplier.id}`}
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>
        {supplier.contact_person && <p className="text-sm text-text-secondary">{supplier.contact_person}</p>}
        {supplier.phone && <p className="text-sm text-text-muted">{supplier.phone}</p>}
        {supplier.agreed_rates && Object.keys(supplier.agreed_rates).length > 0 && (
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">Agreed Rates</p>
            <div className="space-y-1">
              {Object.entries(supplier.agreed_rates).slice(0, 4).map(([item, rate]) => (
                <div key={item} className="flex justify-between text-sm">
                  <span className="text-text-secondary">{item}</span>
                  <span className="font-semibold">₹{rate}/unit</span>
                </div>
              ))}
              {Object.keys(supplier.agreed_rates).length > 4 && (
                <p className="text-xs text-text-muted italic">+{Object.keys(supplier.agreed_rates).length - 4} more...</p>
              )}
            </div>
          </div>
        )}
      </div>
      {editing && (
        <EditSupplierRatesDialog 
          supplier={supplier} 
          onClose={() => setEditing(false)} 
          onSaved={() => { onRefresh(); setEditing(false); }}
        />
      )}
    </>
  );
};

const EditSupplierRatesDialog = ({ supplier, onClose, onSaved }) => {
  const { API } = useClient();
  const [ratesText, setRatesText] = useState(
    Object.entries(supplier.agreed_rates || {}).map(([k, v]) => `${k}: ${v}`).join('\n')
  );
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get(`${API}/price-history/${supplier.client_id}?entity_type=supplier_rate&entity_id=${supplier.id}`)
      .then(res => setHistory(res.data.history || []))
      .catch(() => {});
  }, [supplier.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const rates = {};
    ratesText.split('\n').forEach(line => {
      const [name, rate] = line.split(':').map(s => s.trim());
      if (name && rate && !isNaN(parseFloat(rate))) {
        rates[name] = parseFloat(rate);
      }
    });
    
    setSaving(true);
    try {
      await axios.patch(`${API}/suppliers/${supplier.id}/rates`, { rates });
      onSaved();
    } catch (err) {
      alert('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Rates: {supplier.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">
              Agreed Rates (one per line: "Item: rate")
            </label>
            <textarea
              value={ratesText}
              onChange={(e) => setRatesText(e.target.value)}
              className="w-full border border-border rounded-md px-3 py-2 h-40 text-sm font-mono"
              placeholder="Tomato: 35&#10;Onion: 28"
              data-testid="edit-rates-textarea"
            />
            <p className="text-xs text-text-muted mt-1">Changes are tracked in price history. Use this when renegotiating contracts.</p>
          </div>

          {history.length > 0 && (
            <div className="border-t border-border pt-3">
              <p className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">Recent Rate Changes</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {history.slice(0, 10).map((h, idx) => (
                  <div key={idx} className="text-xs flex justify-between text-text-secondary bg-background-secondary rounded px-2 py-1">
                    <span>{h.item_name}: ₹{h.old_value} → ₹{h.new_value}</span>
                    <span className="text-text-muted">{new Date(h.changed_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary-hover" data-testid="save-rates-button">
              {saving ? 'Saving...' : 'Save Rates'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SummaryCard = ({ label, value, highlight }) => (
  <div className={`bg-white border ${highlight ? 'border-state-error' : 'border-border'} rounded-md p-4`}>
    <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">{label}</p>
    <p className={`text-2xl font-bold ${highlight ? 'text-state-error' : 'text-primary'}`}>{value}</p>
  </div>
);

const AddSupplierDialog = ({ clientId, onAdded }) => {
  const { API } = useClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', category: '', contact_person: '', phone: '', email: '', agreed_rates_text: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Parse agreed_rates from textarea: "Item Name: rate" per line
    const agreed_rates = {};
    form.agreed_rates_text.split('\n').forEach(line => {
      const [name, rate] = line.split(':').map(s => s.trim());
      if (name && rate) agreed_rates[name] = parseFloat(rate);
    });
    
    try {
      await axios.post(`${API}/suppliers`, {
        client_id: clientId,
        name: form.name,
        category: form.category,
        contact_person: form.contact_person || null,
        phone: form.phone || null,
        email: form.email || null,
        agreed_rates
      });
      setOpen(false);
      setForm({ name: '', category: '', contact_person: '', phone: '', email: '', agreed_rates_text: '' });
      onAdded();
    } catch (err) {
      alert('Error adding supplier');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-primary text-primary" data-testid="add-supplier-button">
          <PlusCircle className="mr-2 h-4 w-4" /> Add Supplier
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add New Supplier</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input type="text" placeholder="Supplier Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="supplier-name-input" />
          <input type="text" placeholder="Category (e.g., Vegetables, Meat)" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="supplier-category-input" />
          <input type="text" placeholder="Contact Person" value={form.contact_person} onChange={(e) => setForm({...form, contact_person: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="supplier-contact-input" />
          <input type="text" placeholder="Phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="supplier-phone-input" />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="supplier-email-input" />
          <div>
            <label className="block text-xs uppercase tracking-wider text-text-secondary font-semibold mb-1">Agreed Rates (one per line: "Item: rate")</label>
            <textarea placeholder="Tomato: 35&#10;Onion: 28&#10;Potato: 22" value={form.agreed_rates_text} onChange={(e) => setForm({...form, agreed_rates_text: e.target.value})} className="w-full border border-border rounded-md px-3 py-2 h-24 text-sm" data-testid="supplier-rates-textarea" />
          </div>
          <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-supplier-button">Add Supplier</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddInvoiceDialog = ({ clientId, suppliers, onAdded }) => {
  const { API } = useClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_id: '', invoice_number: '', date: new Date().toISOString().slice(0, 10),
    item_name: '', quantity: '', unit: 'kg', unit_price: '', total_amount: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/suppliers/invoices`, {
        client_id: clientId,
        supplier_id: form.supplier_id,
        invoice_number: form.invoice_number,
        date: form.date,
        item_name: form.item_name,
        quantity: parseFloat(form.quantity),
        unit: form.unit,
        unit_price: parseFloat(form.unit_price),
        total_amount: parseFloat(form.total_amount) || parseFloat(form.unit_price) * parseFloat(form.quantity)
      });
      setOpen(false);
      setForm({ supplier_id: '', invoice_number: '', date: new Date().toISOString().slice(0, 10), item_name: '', quantity: '', unit: 'kg', unit_price: '', total_amount: '' });
      onAdded();
      if (res.data.variance_amount > 0) {
        alert(`Invoice added. ⚠️ Overpaid by ₹${(res.data.variance_amount ?? 0).toFixed(2)} compared to agreed rate`);
      }
    } catch (err) {
      alert('Error adding invoice');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary-hover" data-testid="add-invoice-button">
          <FileText className="mr-2 h-4 w-4" /> Add Invoice
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Supplier Invoice</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <select value={form.supplier_id} onChange={(e) => setForm({...form, supplier_id: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-supplier-select">
            <option value="">Select supplier</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="text" placeholder="Invoice Number" value={form.invoice_number} onChange={(e) => setForm({...form, invoice_number: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-number-input" />
          <input type="date" value={form.date} onChange={(e) => setForm({...form, date: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-date-input" />
          <input type="text" placeholder="Item Name (must match agreed rate)" value={form.item_name} onChange={(e) => setForm({...form, item_name: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-item-input" />
          <div className="grid grid-cols-3 gap-2">
            <input type="number" step="0.01" placeholder="Qty" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-qty-input" />
            <select value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className="w-full border border-border rounded-md px-3 py-2">
              <option value="kg">kg</option>
              <option value="ltr">ltr</option>
              <option value="pcs">pcs</option>
              <option value="g">g</option>
            </select>
            <input type="number" step="0.01" placeholder="Unit ₹" value={form.unit_price} onChange={(e) => setForm({...form, unit_price: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" required data-testid="invoice-unit-price-input" />
          </div>
          <input type="number" step="0.01" placeholder="Total Amount (auto-calculated if empty)" value={form.total_amount} onChange={(e) => setForm({...form, total_amount: e.target.value})} className="w-full border border-border rounded-md px-3 py-2" data-testid="invoice-total-input" />
          <Button type="submit" className="w-full bg-primary hover:bg-primary-hover" data-testid="submit-invoice-button">Add Invoice</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
